import { FC, useRef } from 'react'
import LocationIcon from '@components/Location/reusable/LocationIcon'
import Title from '@components/Location/reusable/Title'
import Subtitle from '@components/Location/reusable/Subtitle'
import LocationSearchByZip from '@components/Location/reusable/LocationSearchByZip'
import styles from '@components/Location/Location.module.scss'
import LocationCard from '@components/Location/reusable/LocationCard'
import Map from '@components/Location/reusable/Map'
import {
  cardsRefType,
  LocationProps,
  OnCardClickType,
  OnMarkerClickType,
} from '@components/Location/location.types'
import useMap from '@components/Location/reusable/Map/useMap'
import NoLocationsFound from '@components/Location/reusable/NoLocationsFound'
import { organizeLocations } from '@components/Location/utils/locationFunctions'

import _isEqual from 'lodash/isEqual'

const Desktop: FC<LocationProps> = ({
  allLocations,
  locationsInArea,
  zipCode,
  zipCodePosition,
}) => {
  const map = useMap({
    allLocations,
    locationsInArea,
    zipCode,
    zipCodePosition,
  })
  const cardsRef: cardsRefType = useRef(null)

  const {
    state: { locationsOnMap },
    handlers: { handleActiveMarker, changePosition },
  } = map

  const onMarkerClick: OnMarkerClickType = (markerId) => {
    const container = cardsRef.current
    if (container) {
      const targetElement = document.getElementById(`location-card-${markerId}`)

      if (container && targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        })
      }
    }
    handleActiveMarker(markerId)
  }

  const onCardClick: OnCardClickType = ({ lat, lng }) => {
    if (!lat || !lng) return
    changePosition(Number(lat), Number(lng))
  }

  return (
    <div className="hidden lg:block">
      <header className="flex flex-row justify-center items-center gap-2 mx-auto py-8">
        <LocationIcon />
        <Title />
        <br />
        <Subtitle />
      </header>
      <div className="flex flex-row gap-8 mb-8">
        <div className="flex flex-col gap-4 w-[75rem]">
          <LocationSearchByZip zipCodePosition={zipCodePosition} />
          <div className="border-t border-gray-300" />
          <div className="flex flex-row items-center gap-2">
            <LocationIcon />
            <h3 className="font-bold">
              {locationsOnMap.length}{' '}
              {zipCode &&
              _isEqual(
                map.state.mapInstance?.getBounds(),
                map.state.initialBoundaries
              )
                ? `Locations near ${zipCode}`
                : 'Locations in the Area'}
            </h3>
          </div>
          <div
            ref={cardsRef}
            style={{ height: '60vh' }}
            className={`flex flex-col gap-2 overflow-y-auto ${styles.customScrollbar}`}
          >
            {locationsOnMap.length ? (
              organizeLocations(locationsOnMap, locationsInArea).map(
                (location) => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    onCardClick={onCardClick}
                  />
                )
              )
            ) : (
              <NoLocationsFound />
            )}
          </div>
        </div>

        <Map
          {...map}
          onMarkerClick={onMarkerClick}
          allLocations={allLocations}
        />
      </div>
    </div>
  )
}

export default Desktop
